import React, { useState, useEffect } from 'react';
import '../ForBrands/brandMode.css';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE_URL } from '../../constants';
import {
  Badge,
  BmModal,
  Btn,
  Card,
  ConfirmModal,
  Dot,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Loading,
  MetricCard,
  SearchBox,
  Select,
  TextArea,
} from '../ForBrands/ui/BrandUI';
import { Icon } from '../ForBrands/ui/icons';
import {
  POSITIVE_DOT,
  PaymentStatusBadge,
  PaymentsPager,
  TD_CLASS,
  TD_MUTED_CLASS,
  TH_CLASS,
  TR_CLASS,
} from './paymentStatus';

// bm-critical in tailwind.config.js.
const CRITICAL_DOT = '#e2241a';

// Format currency with commas
const formatCurrency = (amount) => {
  return Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// The delete dialog's line: the invoice number and customer when known.
const deleteBody = (invoice) => {
  const which = invoice.invoice_number ? `Invoice ${invoice.invoice_number}` : 'This invoice';
  const whom = invoice.user_email ? ` for ${invoice.user_email}` : '';
  return `${which}${whom} will be deleted.`;
};

const DotLabel = ({ color, children }) => (
  <span className="inline-flex items-center gap-2">
    <Dot color={color} />
    {children}
  </span>
);

const InvoiceManagementTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activateTierModal, setActivateTierModal] = useState({ isOpen: false, invoice: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const token = useSelector((state) => state.auth.token);

  // Fetch invoices
  const fetchInvoices = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const params = {
        page,
        limit,
        ...(statusFilter !== 'all' && { status: statusFilter })
      };

      const response = await axios.get(`${API_BASE_URL}/invoices/`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setInvoices(response.data.invoices);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setLoadError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/invoices/stats/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, statusFilter]);

  // Send invoice
  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(
        `${API_BASE_URL}/invoices/${invoiceId}/send/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invoice sent successfully!');
      fetchInvoices();
    } catch (error) {
      console.error('Error sending invoice:', error);
      toast.error('Failed to send invoice');
    }
  };

  // Mark as paid
  const handleMarkAsPaid = async (invoiceId) => {
    try {
      await axios.put(
        `${API_BASE_URL}/invoices/${invoiceId}/`,
        { status: 'paid' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Failed to mark invoice as paid');
    }
  };

  // Delete invoice (after the ConfirmModal)
  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await axios.delete(`${API_BASE_URL}/invoices/${invoiceId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const confirmDeleteInvoice = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await handleDeleteInvoice(deleteTarget._id);
    setDeleting(false);
    setDeleteTarget(null);
  };

  // Open activate tier modal
  const handleActivateTier = (invoice) => {
    setActivateTierModal({ isOpen: true, invoice });
  };

  // Confirm tier activation
  const confirmActivateTier = async () => {
    const invoiceId = activateTierModal.invoice._id;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/invoices/${invoiceId}/activate-tier/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Success! ${response.data.message}`, {
        description: `Tier: ${response.data.tier}. Valid until: ${new Date(response.data.subscription_end).toLocaleDateString()}`,
      });
      setActivateTierModal({ isOpen: false, invoice: null });
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error activating tier:', error);
      toast.error(error.response?.data?.error || 'Failed to activate tier');
    }
  };

  // The search box narrows the loaded page by invoice number, name or email.
  const query = search.trim().toLowerCase();
  const visibleInvoices = (invoices || []).filter((invoice) => !query
    || [invoice.invoice_number, invoice.user_name, invoice.user_email]
      .some((v) => String(v || '').toLowerCase().includes(query)));

  const renderBody = () => {
    if (loading) return <Loading label="Loading invoices..." />;
    if (loadError) return <ErrorState>{loadError}</ErrorState>;
    if (!invoices || invoices.length === 0) return <EmptyState>No invoices found</EmptyState>;
    if (visibleInvoices.length === 0) return <EmptyState>No invoices on this page match your search</EmptyState>;
    return (
      <Card padding="p-0" className="w-full min-w-0 max-w-full overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-bm-subtle">
              <tr>
                <th className={TH_CLASS}>Invoice #</th>
                <th className={TH_CLASS}>Customer</th>
                <th className={TH_CLASS}>Amount</th>
                <th className={TH_CLASS}>Status</th>
                <th className={TH_CLASS}>Due date</th>
                <th className={`${TH_CLASS} !text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleInvoices.map((invoice) => (
                <tr key={invoice._id} className={TR_CLASS}>
                  <td className={`${TD_CLASS} whitespace-nowrap`}>
                    <span className="bm-label-l">{invoice.invoice_number}</span>
                  </td>
                  <td className={`${TD_CLASS} whitespace-nowrap`}>
                    <div>{invoice.user_name}</div>
                    <div className="bm-body-s text-bm-muted">{invoice.user_email}</div>
                  </td>
                  <td className={`${TD_CLASS} whitespace-nowrap`}>
                    <span className="bm-label-l">${formatCurrency(invoice.amount)} {invoice.currency}</span>
                  </td>
                  <td className={TD_CLASS}>
                    <PaymentStatusBadge status={invoice.status} />
                  </td>
                  <td className={`${TD_MUTED_CLASS} whitespace-nowrap`}>
                    {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
                  </td>
                  <td className={`${TD_CLASS} whitespace-nowrap`}>
                    <div className="flex items-center justify-end gap-2">
                      {invoice.status === 'draft' && (
                        <Btn variant="secondary" size="sm" className="gap-1.5" title="Send invoice" onClick={() => handleSendInvoice(invoice._id)}>
                          <Icon name="mail" size={14} />
                          Send
                        </Btn>
                      )}
                      {invoice.status === 'sent' && (
                        <Btn variant="secondary" size="sm" className="gap-1.5" title="Mark as paid" onClick={() => handleMarkAsPaid(invoice._id)}>
                          <Icon name="check" size={14} />
                          Mark paid
                        </Btn>
                      )}
                      {invoice.status === 'paid' && invoice.tier_request_id && !invoice.tier_activated && (
                        <Btn size="sm" title="Activate tier for coach" onClick={() => handleActivateTier(invoice)}>
                          Activate tier
                        </Btn>
                      )}
                      {invoice.status === 'paid' && invoice.tier_activated && (
                        <Badge tone="outline" dot={POSITIVE_DOT}>Tier activated</Badge>
                      )}
                      <Btn variant="danger" size="sm" className="gap-1.5" title="Delete invoice" onClick={() => setDeleteTarget(invoice)}>
                        <Icon name="trash" size={14} />
                        Delete
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {stats && stats.counts && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard label="Total invoices" value={stats.counts.total || 0} />
          <MetricCard
            label={<DotLabel color={POSITIVE_DOT}>Paid</DotLabel>}
            value={stats.counts.paid || 0}
            sub={`$${formatCurrency(stats.amounts_by_status?.paid || 0)}`}
          />
          <MetricCard label="Sent" value={stats.counts.sent || 0} sub={`$${formatCurrency(stats.amounts_by_status?.sent || 0)}`} />
          <MetricCard
            label={<DotLabel color={CRITICAL_DOT}>Overdue</DotLabel>}
            value={stats.counts.overdue || 0}
            sub={`$${formatCurrency(stats.amounts_by_status?.overdue || 0)}`}
          />
        </div>
      )}

      <Card padding="p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="grid w-full min-w-0 grid-cols-1 gap-4 md:w-auto md:flex-1 md:grid-cols-3">
            <Field label="Search">
              <SearchBox
                value={search}
                onChange={setSearch}
                placeholder="Invoice #, name or email..."
                aria-label="Search invoices"
              />
            </Field>
            <Field label="Status">
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </Field>
            <Field label="Items per page">
              <Select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={30}>30 per page</option>
                <option value={100}>100 per page</option>
              </Select>
            </Field>
          </div>
          <Btn className="gap-2" onClick={() => setShowCreateModal(true)}>
            <Icon name="plus" size={16} />
            Create invoice
          </Btn>
        </div>
      </Card>

      {renderBody()}

      {!loading && !loadError && totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="bm-body-s text-bm-muted">
            Page {page} of {totalPages}
          </div>
          <PaymentsPager page={page} pageCount={totalPages} onChange={setPage} />
        </div>
      )}

      {showCreateModal && (
        <CreateInvoiceModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchInvoices();
            fetchStats();
          }}
          token={token}
        />
      )}

      {activateTierModal.isOpen && activateTierModal.invoice && (
        <ActivateTierConfirmationModal
          invoice={activateTierModal.invoice}
          onClose={() => setActivateTierModal({ isOpen: false, invoice: null })}
          onConfirm={confirmActivateTier}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          tone="danger"
          title="Delete invoice"
          body={deleteBody(deleteTarget)}
          confirmLabel="Delete invoice"
          busy={deleting}
          onConfirm={confirmDeleteInvoice}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

// Create Invoice Modal Component
const CreateInvoiceModal = ({ onClose, onSuccess, token }) => {
  const [formData, setFormData] = useState({
    user_email: '',
    amount: '',
    due_date: '',
    notes: '',
    items: [{ description: '', quantity: 1, price: 0, total: 0 }]
  });
  const [loading, setLoading] = useState(false);

  const handleAddItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, price: 0, total: 0 }]
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    // Calculate total
    if (field === 'quantity' || field === 'price') {
      newItems[index].total = Number(newItems[index].quantity) * Number(newItems[index].price);
    }

    // Calculate grand total
    const grandTotal = newItems.reduce((sum, item) => sum + (item.total || 0), 0);

    setFormData(prev => ({
      ...prev,
      items: newItems,
      amount: grandTotal.toFixed(2)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API_BASE_URL}/invoices/`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Failed to create invoice: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <BmModal title="Create invoice" onClose={onClose} width={760}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Customer email *">
            <Input
              type="email"
              required
              value={formData.user_email}
              onChange={(e) => setFormData(prev => ({ ...prev, user_email: e.target.value }))}
              placeholder="customer@example.com"
            />
          </Field>
          <Field label="Due date">
            <Input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
            />
          </Field>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <span className="bm-label-l text-bm-ink">Line items *</span>
            <Btn variant="secondary" size="sm" className="gap-1.5" onClick={handleAddItem}>
              <Icon name="plus" size={14} />
              Add item
            </Btn>
          </div>

          <div className="hidden grid-cols-12 gap-3 px-4 sm:grid">
            <span className="col-span-5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">Description</span>
            <span className="col-span-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">Quantity</span>
            <span className="col-span-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">Unit price</span>
            <span className="col-span-2 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">Total</span>
          </div>

          {formData.items.map((item, index) => (
            <div key={index} className="rounded-[8px] border border-bm-border bg-bm-subtle p-4">
              <div className="grid grid-cols-12 items-center gap-3">
                <div className="col-span-12 sm:col-span-5">
                  <Input
                    type="text"
                    aria-label={`Item ${index + 1} description`}
                    placeholder="Description"
                    required
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Input
                    type="number"
                    aria-label={`Item ${index + 1} quantity`}
                    placeholder="Qty"
                    required
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                  />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Input
                    type="number"
                    aria-label={`Item ${index + 1} unit price`}
                    placeholder="Price"
                    required
                    min="0"
                    step="0.01"
                    value={item.price}
                    onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                  />
                </div>
                <div className="col-span-3 sm:col-span-2">
                  <span className="bm-label-l text-bm-ink">${formatCurrency(item.total)}</span>
                </div>
                <div className="col-span-1 flex justify-end">
                  {formData.items.length > 1 && (
                    <Btn
                      variant="danger"
                      size="sm"
                      className="!px-2"
                      aria-label={`Remove item ${index + 1}`}
                      title="Remove item"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <Icon name="trash" size={14} />
                    </Btn>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="text-right bm-h-s text-bm-ink">Total: ${formatCurrency(formData.amount || 0)}</div>
        </div>

        <Field label="Notes">
          <TextArea
            rows="3"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Additional notes or payment instructions..."
          />
        </Field>

        <div className="-mx-7 -mb-6 flex items-center gap-3 border-t border-bm-border px-7 pb-5 pt-4">
          <Btn variant="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Btn>
          <Btn type="submit" className="flex-1" disabled={loading}>
            {loading ? 'Creating...' : 'Create invoice'}
          </Btn>
        </div>
      </form>
    </BmModal>
  );
};

// Activate Tier Confirmation Modal Component
const ActivateTierConfirmationModal = ({ invoice, onClose, onConfirm }) => {
  // Get tier display name
  const getTierName = (inv) => {
    const tierNames = {
      'COACH_CORE': 'Core Subscription',
      'COACH_PRO': 'Pro Subscription',
      'COACH_PREMIUM': 'Premium Subscription'
    };
    const tier = inv.metadata?.tier;
    return tierNames[tier] || 'Subscription';
  };

  const rows = [
    ['Invoice number', invoice.invoice_number],
    ['Coach email', invoice.user_email],
    ['Coach name', invoice.user_name || 'N/A'],
    ['Tier', getTierName(invoice)],
    ['Amount paid', `$${formatCurrency(invoice.amount)} ${invoice.currency || ''}`.trim()],
  ];

  return (
    <BmModal
      title="Activate tier subscription"
      onClose={onClose}
      footer={
        <>
          <Btn variant="secondary" className="flex-1" onClick={onClose}>
            Cancel
          </Btn>
          <Btn className="flex-1" onClick={onConfirm}>
            Confirm activation
          </Btn>
        </>
      }
    >
      <p className="bm-body-m text-bm-muted">
        You are about to activate a <span className="bm-label-l text-bm-ink">1-year subscription</span> for this coach.
        Please confirm the details below:
      </p>

      <dl className="mt-4 flex flex-col gap-3 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-[14px]">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-start justify-between gap-4">
            <dt className="bm-body-s text-bm-muted">{label}</dt>
            <dd className="text-right bm-label-l text-bm-ink">{value}</dd>
          </div>
        ))}
        <div className="flex items-start justify-between gap-4 border-t border-bm-border pt-3">
          <dt className="bm-body-s text-bm-muted">Subscription duration</dt>
          <dd className="text-right bm-label-l text-bm-ink">1 year (annual)</dd>
        </div>
      </dl>

      <p className="mt-4 bm-body-s text-bm-muted">
        <span className="bm-label-m text-bm-ink">Note:</span> This action will immediately grant the coach access to their paid tier for 1 year from today.
      </p>
    </BmModal>
  );
};

export default InvoiceManagementTab;
